// AI credits gauge for the /billing hub page and the /billing/credits detail page.
// See plans/saas-billing/03-billing-portal-page.md and plans/saas-billing/08-ai-credit-usage-display.md.
//
// One component, two variants:
//   - 'compact'  — small card used on /billing and in the app-shell sidebar
//                  (and the bottom tab bar on mobile). Default.
//   - 'detailed' — larger card used on /billing/credits with the breakdown
//                  summary and prominent CTA.
//
// Both variants subscribe to the same AICreditBalance, so the sidebar and
// detail page stay in sync without an explicit refresh.
//
// The gauge is usage-first:
//   - the bar represents used credits (not remaining balance)
//   - the primary label shows used vs allocated
//   - remaining credits and renewal timing are explicit secondary text
//
// Color ramp follows percent used:
//   - < 80% used                           — green
//   - 80% - <95% used                      — yellow
//   - 95% - <100% used                     — orange
//   - 100% used AND topUpBalance === 0     — red
//   - 100% used AND topUpBalance > 0       — orange

import { CommonModule } from '@angular/common';
import { Component, Input, Optional } from '@angular/core';
import { Router } from '@angular/router';
import {
  IonCard,
  IonCardContent,
  IonCardHeader,
  IonCardTitle,
  IonProgressBar
} from '@ionic/angular';

import { AICreditBalance } from '../../models/billing';
import { LoggerService } from '../../services/logger.service';
import { TranslatePipe } from '../../pipes/translate.pipe';
import { TranslationParams, TranslationService } from '../../services/translation.service';
import { billingText } from './billing-text';

export type BillingCreditsGaugeVariant = 'compact' | 'detailed';
export type BillingCreditsGaugeSeverity = 'green' | 'yellow' | 'orange' | 'red';

type CreditUsageBalance = Pick<
  AICreditBalance,
  'allocatedCredits' | 'usedCredits' | 'reservedCredits' | 'topUpBalance'
>;

export function getCycleRemaining(balance: CreditUsageBalance | null | undefined): number {
  if (!balance) {
    return 0;
  }
  return Math.max(0, balance.allocatedCredits - balance.usedCredits - balance.reservedCredits);
}

export function getCreditUsageRatio(
  balance: Pick<AICreditBalance, 'allocatedCredits' | 'usedCredits'> | null | undefined
): number {
  if (!balance || balance.allocatedCredits <= 0) {
    return 0;
  }
  return Math.max(0, Math.min(1, balance.usedCredits / balance.allocatedCredits));
}

export function getCreditSeverity(
  balance: CreditUsageBalance | null | undefined
): BillingCreditsGaugeSeverity {
  if (!balance || balance.allocatedCredits <= 0) {
    return 'green';
  }

  const ratio = getCreditUsageRatio(balance);
  if (ratio >= 1) {
    return balance.topUpBalance > 0 ? 'orange' : 'red';
  }
  if (ratio >= 0.95) {
    return 'orange';
  }
  if (ratio >= 0.8) {
    return 'yellow';
  }
  return 'green';
}

export function formatCreditRenewalDate(iso: string): string {
  const ts = Date.parse(iso);
  if (Number.isNaN(ts)) {
    return 'next cycle';
  }
  return new Date(ts).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  });
}

export function formatCreditRenewalLabel(iso: string): string {
  const renewalDate = formatCreditRenewalDate(iso);
  return renewalDate === 'next cycle' ? 'Renews next cycle' : `Renews ${renewalDate}`;
}

@Component({
    selector: 'app-billing-credits-gauge',
    imports: [
        CommonModule,
        IonCard,
        IonCardHeader,
        IonCardTitle,
        IonCardContent,
        IonProgressBar,
        TranslatePipe
    ],
    template: `
    <ion-card
      class="billing-card billing-card--{{ variant }} billing-card--severity-{{ severity }}"
      [attr.data-testid]="'billing-credits-gauge-card'"
      [attr.data-variant]="variant"
      [attr.data-severity]="severity">
      <ion-card-header>
        <ion-card-title>{{ headerTitle }}</ion-card-title>
      </ion-card-header>
      <ion-card-content>
        @if (balance) {
          <ion-progress-bar
            class="credits-bar credits-bar--severity-{{ severity }}"
            [value]="usageRatio"
            data-testid="billing-credits-progress"
          ></ion-progress-bar>
        }
        <div class="credits-label" data-testid="billing-credits-label">
          {{ 'billing.creditsGauge.usedOfAllocated' | translate: { used: (balance.usedCredits | number), allocated: (balance.allocatedCredits | number) } }}
        </div>
        <div class="credits-meta" data-testid="billing-credits-meta">
          <span data-testid="billing-credits-remaining">{{ 'billing.creditsGauge.remaining' | translate: { value: (cycleRemaining | number) } }}</span>
          <span class="credits-meta__separator" aria-hidden="true">&bull;</span>
          <span data-testid="billing-credits-renewal">{{ renewalLabel }}</span>
        </div>
        @if (balance.reservedCredits > 0) {
          <div
            class="credits-reserved"
            data-testid="billing-credits-reserved">
            {{ 'billing.creditsGauge.inFlightNote' | translate: { value: (balance.reservedCredits | number) } }}
          </div>
        }
        @if (balance.topUpBalance > 0) {
          <div
            class="credits-topup"
            data-testid="billing-credits-topup">
            {{ 'billing.creditsGauge.topUpNote' | translate: { value: (balance.topUpBalance | number) } }}
          </div>
        }
    
        <!-- Detailed-variant breakdown summary. Stable getters; no new array refs. -->
        @if (variant === 'detailed') {
          <div class="credits-breakdown" data-testid="billing-credits-breakdown">
            <div class="credits-breakdown__row">
              <span class="credits-breakdown__label">{{ 'billing.credits.allocated' | translate }}</span>
              <span class="credits-breakdown__value">{{ balance.allocatedCredits | number }}</span>
            </div>
            <div class="credits-breakdown__row">
              <span class="credits-breakdown__label">{{ 'billing.credits.used' | translate }}</span>
              <span class="credits-breakdown__value">{{ balance.usedCredits | number }}</span>
            </div>
            <div class="credits-breakdown__row">
              <span class="credits-breakdown__label">{{ 'billing.credits.remainingCycle' | translate }}</span>
              <span class="credits-breakdown__value">{{ cycleRemaining | number }}</span>
            </div>
            @if (balance.reservedCredits > 0) {
              <div class="credits-breakdown__row">
                <span class="credits-breakdown__label">{{ 'billing.creditsGauge.inFlight' | translate }}</span>
                <span class="credits-breakdown__value">{{ balance.reservedCredits | number }}</span>
              </div>
            }
            @if (balance.topUpBalance > 0) {
              <div class="credits-breakdown__row">
                <span class="credits-breakdown__label">{{ 'billing.credits.topUpBalance' | translate }}</span>
                <span class="credits-breakdown__value">{{ balance.topUpBalance | number }}</span>
              </div>
            }
            <div class="credits-breakdown__row">
              <span class="credits-breakdown__label">{{ 'billing.creditsGauge.renewsLabel' | translate }}</span>
              <span class="credits-breakdown__value">{{ renewalDate }}</span>
            </div>
          </div>
        }
    
        <div class="credits-actions" data-testid="billing-credits-actions">
          <button
            type="button"
            class="credits-cta credits-cta--primary"
            (click)="onBuyMore()"
            data-testid="billing-buy-more-credits">
            {{ 'billing.credits.buyMore' | translate }}
          </button>
          <!-- Compact variant: secondary link CTA. At 80% usage or higher,
          swap "View details" for "Top up" so the more urgent action
          is one tap away (M4 fix). -->
          @if (variant === 'compact' && usageRatio < 0.8) {
            <button
              type="button"
              class="credits-cta credits-cta--link"
              (click)="onViewDetails()"
              data-testid="billing-view-credit-details">
              {{ 'billing.creditsGauge.viewDetails' | translate }}
            </button>
          }
          @if (variant === 'compact' && usageRatio >= 0.8) {
            <button
              type="button"
              class="credits-cta credits-cta--link"
              (click)="onTopUp()"
              data-testid="billing-top-up-credits">
              {{ 'billing.creditsGauge.topUp' | translate }}
            </button>
          }
        </div>
      </ion-card-content>
    </ion-card>
    `,
    styles: [
        `
      .billing-card {
        margin: 0;
      }
      .billing-card--detailed {
        padding: 4px 0;
      }
      .credits-bar {
        margin: 6px 0 12px;
        --background: #e5e7eb;
        --progress-background: #16a34a;
        height: 8px;
        border-radius: 4px;
        overflow: hidden;
      }
      .billing-card--detailed .credits-bar {
        height: 14px;
        margin: 8px 0 16px;
      }
      .credits-bar--severity-green {
        --progress-background: #16a34a;
      }
      .credits-bar--severity-yellow {
        --progress-background: #eab308;
      }
      .credits-bar--severity-orange {
        --progress-background: #f97316;
      }
      .credits-bar--severity-red {
        --progress-background: #dc2626;
      }
      .credits-label {
        font-size: 14px;
        color: var(--ion-text-color, #111827);
        font-weight: 500;
      }
      .billing-card--detailed .credits-label {
        font-size: 16px;
      }
      .credits-meta {
        margin-top: 4px;
        display: flex;
        align-items: center;
        gap: 6px;
        flex-wrap: wrap;
        font-size: 13px;
        color: var(--ion-color-medium, #6b7280);
      }
      .credits-meta__separator {
        line-height: 1;
      }
      .credits-reserved {
        margin-top: 4px;
        font-size: 13px;
        color: var(--ion-color-medium, #6b7280);
      }
      .credits-topup {
        margin-top: 4px;
        font-size: 13px;
        color: var(--ion-color-medium);
      }
      .credits-breakdown {
        margin-top: 16px;
        display: grid;
        grid-template-columns: 1fr;
        gap: 6px;
      }
      .credits-breakdown__row {
        display: flex;
        justify-content: space-between;
        font-size: 14px;
      }
      .credits-breakdown__label {
        color: var(--ion-color-medium);
      }
      .credits-breakdown__value {
        font-weight: 500;
      }
      .credits-actions {
        margin-top: 16px;
        display: flex;
        gap: 10px;
        align-items: center;
        flex-wrap: wrap;
      }
      .credits-cta {
        padding: 8px 14px;
        background: transparent;
        color: var(--ion-color-primary, #2563eb);
        border: 1px solid var(--ion-color-primary, #2563eb);
        border-radius: 6px;
        font-weight: 500;
        font-size: 14px;
        cursor: pointer;
      }
      .credits-cta:hover {
        background: rgba(59, 130, 246, 0.08);
      }
      .credits-cta--primary {
        background: var(--ion-color-primary, #2563eb);
        color: #fff;
      }
      .credits-cta--primary:hover {
        background: #1d4ed8;
      }
      .credits-cta--link {
        border: none;
        padding: 8px 4px;
      }
    `
    ]
})
export class BillingCreditsGaugeComponent {
  @Input({ required: true }) balance!: AICreditBalance;
  @Input() variant: BillingCreditsGaugeVariant = 'compact';

  constructor(
    private readonly router: Router,
    private readonly logger: LoggerService,
    @Optional() private readonly translations?: TranslationService
  ) {}

  get headerTitle(): string {
    return this.variant === 'detailed'
      ? this.t('billing.creditsGauge.titleDetailed', 'AI credits this cycle')
      : this.t('billing.creditsGauge.title', 'AI credits');
  }

  // Cycle-only remaining (excludes top-up balance). The progress bar is usage-
  // first, while remaining balance and renewal timing are called out in text.
  get cycleRemaining(): number {
    return getCycleRemaining(this.balance);
  }

  get usageRatio(): number {
    return getCreditUsageRatio(this.balance);
  }

  get renewalLabel(): string {
    if (!this.balance || Number.isNaN(Date.parse(this.balance.billingCycleEnd))) {
      return this.t('billing.creditsGauge.renewsNextCycle', 'Renews next cycle');
    }
    return this.t('billing.creditsGauge.renewsOn', 'Renews {{date}}', {
      date: formatCreditRenewalDate(this.balance.billingCycleEnd)
    });
  }

  get renewalDate(): string {
    if (!this.balance || Number.isNaN(Date.parse(this.balance.billingCycleEnd))) {
      return this.t('billing.creditsGauge.nextCycle', 'next cycle');
    }
    return formatCreditRenewalDate(this.balance.billingCycleEnd);
  }

  get severity(): BillingCreditsGaugeSeverity {
    return getCreditSeverity(this.balance);
  }

  onBuyMore(): void {
    this.logger.info(
      'BillingCreditsGaugeComponent',
      'onBuyMore',
      'navigating to top-up page',
      { variant: this.variant }
    );
    this.router.navigateByUrl('/billing/credits/top-up');
  }

  onViewDetails(): void {
    this.logger.info(
      'BillingCreditsGaugeComponent',
      'onViewDetails',
      'navigating to /billing/credits',
      {}
    );
    this.router.navigateByUrl('/billing/credits');
  }

  // M4: shown in place of the "View details" link on the compact variant when
  // usageRatio >= 80%. Routes to the same top-up surface as the primary CTA.
  onTopUp(): void {
    this.logger.info(
      'BillingCreditsGaugeComponent',
      'onTopUp',
      'compact-low secondary CTA - navigating to top-up page',
      { variant: this.variant }
    );
    this.router.navigateByUrl('/billing/credits/top-up');
  }

  private t(key: string, fallback: string, params?: TranslationParams): string {
    return billingText(this.translations, key, fallback, params);
  }
}
